import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import momentTz from 'moment-timezone';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetDiscountCodeReport } from '../../../hooks/reports/queries';
import { exportDiscountCodesReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { BarChart } from '@mantine/charts';
import { getRangeDate } from '../datePickerHelper';
import type { DiscountReportRow } from 'washday-sdk';

export type DiscountCodesTableInfoData = DiscountReportRow;
const DiscountCodesReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRangePicker] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const [appliedDateRange, setAppliedDateRange] = useState(dateRangePicker);
  const { fromDate, toDate } = getRangeDate(appliedDateRange);

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetDiscountCodeReport(searchParamStoreId, { fromDate, toDate });

  const tableInfoData: DiscountCodesTableInfoData[] = useMemo(() => {
    return data?.tableInfo || [];
  }, [data]);
  const graphData = useMemo(() => (
    (data?.graphData || []).map((row) => row.definitionMissing
      ? {
        ...row,
        label: t('ReportsPage:discountCodesReport.deletedDiscount')
      }
      : row)
  ), [data, t]);
  const totalRowsCount = useMemo(() => data?.tableInfo?.length || 0, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:discountCodesReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={(value) => {
              setDateRangePicker(value);
              if (!value[0] || !value[1]) return;

              setAppliedDateRange(value);
              setPagination((current) => ({ ...current, pageIndex: 0 }));
            }}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col
          span={{ xs: 12, md: 'content' }}
          style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportDiscountCodesReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: momentTz.tz.guess(true)
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <BarChart
            h={300}
            data={graphData}
            dataKey="label"
            series={[
              {
                name: 'usedCounter',
                color: 'blue.6',
                label: t('ReportsPage:discountCodesReport.applications')
              }
            ]}
            tickLine="y"
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default DiscountCodesReport;
